import { ref, onUnmounted, onMounted } from 'vue';

interface PeriodicTaskOptions {
  intervalMs: number;
  immediate?: boolean;
  onError?: (error: Error) => void;
}

export function usePeriodicTask(
  task: () => Promise<void> | void,
  options: PeriodicTaskOptions
) {
  const intervalId = ref<number | null>(null);
  const isRunning = ref(false);
  const lastRunTime = ref<number | null>(null);
  const errorCount = ref(0);

  const start = () => {
    if (isRunning.value) {
      return; // Already running
    }

    isRunning.value = true;

    const executeTask = async () => {
      try {
        lastRunTime.value = Date.now();
        await task();
        errorCount.value = 0; // Reset error count on success
      } catch (error) {
        errorCount.value++;
        console.error(`Periodic task failed (attempt ${errorCount.value}):`, error);
        options.onError?.(error as Error);
      }
    };

    // Execute immediately if requested
    if (options.immediate) {
      executeTask();
    }

    // Set up the interval
    intervalId.value = window.setInterval(executeTask, options.intervalMs);
  };

  const stop = () => {
    if (intervalId.value !== null) {
      window.clearInterval(intervalId.value);
      intervalId.value = null;
    }
    isRunning.value = false;
  };

  const restart = () => {
    stop();
    start();
  };

  // Auto-cleanup on component unmount
  onUnmounted(() => {
    stop();
  });

  return {
    isRunning,
    lastRunTime,
    errorCount,
    start,
    stop,
    restart
  };
}
